import { format as formatDate } from 'date-fns';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { ColumnDef, CellContext } from '@tanstack/react-table';
import { Icon } from '../../components/shared/icons/Icon';
import { InfoChitImportance } from '../../components/shared/InfoChit';
import { TableChit } from '../../components/shared/table/TableChit';
import TableContents from '../../components/shared/table/TableContents';
import { TableExpander } from '../../components/shared/table/TableExpander';
import { ClockMethodToString } from '../../components/shared/table/Utils';
import { displayPay, getPayFromDuration } from '../../helpers/calculationsSummaryShiftsReportHelper';
import { calculateMinutesDuration, displayMinutesAsHoursMinutes } from '../../helpers/dateHelper';
import {
  EmployeeWorkTimeRecord,
  ReadEmployee,
  WorkTimeDataRisk,
  WorkTimeDataSource
} from '../../services/gql/graphql.generated';
import { useLocationService } from '../../services/location/LocationService';
import { ShiftDetailsView } from './detailsView/ShiftDetailsView';
import strings from './shifts.translations';
import { EmployeeAvatar } from '../home/clockIn/EmployeeAvatar';
import { useFeatures } from '@/services/backstage/useFeatures';

export interface ShiftsTableProps {
  startDate: Date;
  endDate: Date;
  userId?: string;
  recordId?: string;
  needsRefetch?: boolean;
  refetchComplete?: () => void;
  onShiftEdit?: () => void;
  data?: EmployeeWorkTimeRecord[];
  refetch?: () => void;
  isMe?: boolean;
}

export interface ShiftsTableRow extends Record<string, unknown> {
  name: string;
  dateInfo: {
    date: Date;
    start: Date;
    end?: Date;
    duration?: number;
    pay?: number;
  };
  record: EmployeeWorkTimeRecord;
  risk?: WorkTimeDataRisk;
  clockInMethod?: WorkTimeDataSource;
  clockOutMethod?: WorkTimeDataSource;
  locationId: number;
  onShiftEdit?: () => void;
  isMe?: boolean;
  employee: ReadEmployee;
}

export const ShiftsTable: FC<ShiftsTableProps> = ({
  startDate,
  endDate,
  userId,
  recordId,
  needsRefetch,
  refetchComplete,
  onShiftEdit,
  data,
  refetch,
  isMe
}) => {
  const pay = useFeatures('pay');

  useEffect(() => {
    if (recordId) {
      document.querySelector(`#${'row-' + recordId}`)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [recordId]);

  const startDateForQuery = new Date(startDate);
  startDateForQuery.setHours(0, 0, 0, 0);

  const endDateForQuery = new Date(endDate);
  endDateForQuery.setHours(0, 0, 0, 0);
  //We want to include the end date, so set it to midnight on the day after.
  endDateForQuery.setDate(endDateForQuery.getDate() + 1);

  const locationList = useLocationService(state => state.locationList);

  useEffect(() => {
    if (needsRefetch && refetchComplete) {
      refetch && refetch();
      refetchComplete();
    }
  }, [needsRefetch, refetchComplete, refetch]);

  const riskToImportance = (risk?: WorkTimeDataRisk) => {
    switch (risk) {
      case WorkTimeDataRisk.High:
        return InfoChitImportance.HIGH;
      case WorkTimeDataRisk.Medium:
        return InfoChitImportance.MEDIUM;
      case WorkTimeDataRisk.Low:
        return InfoChitImportance.LOW;
      default:
        return InfoChitImportance.LOW;
    }
  };

  const formatMobileDateTime = (date: Date, start: Date, end?: Date) => {
    let mobileDate = formatDate(date, 'do MMM');
    mobileDate += ` ${formatDate(start, 'HH:mm')}`;
    if (end) {
      mobileDate += ` - `;
      mobileDate += formatDate(end, 'HH:mm');
    }
    return mobileDate;
  };

  const rows: ShiftsTableRow[] = useMemo(() => {
    if (!data) return [];

    const sortedRecords = [...data].sort((a, b) => {
      if ((a.start?.dateTime || 0) > (b.start?.dateTime || 0)) {
        return -1;
      } else {
        return 1;
      }
    });

    return sortedRecords.map(record => {
      const duration = calculateMinutesDuration(record.start?.dateTime, record.end?.dateTime || new Date());

      return {
        name: strings.employeeName(record?.employee?.firstName ?? '', record?.employee?.lastName ?? ''),
        dateInfo: {
          date: new Date(record.start?.dateTime),
          start: new Date(record.start?.dateTime),
          end: record.end ? new Date(record.end?.dateTime) : undefined,
          duration: duration,
          pay: getPayFromDuration(duration, 0)
        },
        record: record as EmployeeWorkTimeRecord,
        risk: record.risk,
        clockInMethod: record.start?.origin,
        clockOutMethod: record.end?.origin,
        onShiftEdit: onShiftEdit,
        isMe: isMe,
        locationId: record.locationId,
        employee: record.employee as ReadEmployee
      };
    });
  }, [data, onShiftEdit, isMe]);

  const sortRisk = useCallback((rowA: any, rowB: any) => {
    const importanceA = riskToImportance(rowA.original.risk);
    const importanceB = riskToImportance(rowB.original.risk);
    if (importanceA > importanceB) return 1;
    if (importanceB > importanceA) return -1;
    return 0;
  }, []);

  const buildImportanceChit = useCallback((info: CellContext<ShiftsTableRow, any>) => {
    const value = info.getValue();
    switch (value) {
      case WorkTimeDataRisk.Low:
        return <div>{'-'}</div>;
      case WorkTimeDataRisk.Medium:
        return <Icon Icon={FiAlertTriangle} theme="amber" />;
      case WorkTimeDataRisk.High:
        return <Icon Icon={FiAlertTriangle} theme="red" />;
      default:
        return <div>{'-'}</div>;
    }
  }, []);

  const buildClockTime = useCallback((info: CellContext<ShiftsTableRow, any>) => {
    const value = info.getValue();
    return value ? <>{formatDate(new Date(value), 'HH:mm')}</> : <TableChit>{value}</TableChit>;
  }, []);

  const buildClockMethod = useCallback((info: CellContext<ShiftsTableRow, any>) => {
    return <TableChit>{ClockMethodToString(info.getValue())}</TableChit>;
  }, []);

  const buildLocation = useCallback(
    (info: CellContext<ShiftsTableRow, any>) => {
      const location = locationList.find(loc => loc.id === info.getValue());
      return <TableChit>{location?.name}</TableChit>;
    },
    [locationList]
  );

  const desktopColumns = useMemo(() => {
    const optionalColumns: (ColumnDef<ShiftsTableRow, any> | null)[] = [
      !userId
        ? {
            header: '',
            accessorKey: 'avatar',
            cell: info => (
              <EmployeeAvatar
                size={40}
                employee={{
                  id: info.row.original.employee.identityId,
                  firstName: '',
                  lastName: '',
                  avatarUrl: info.row.original.employee.avatarUrl
                }}
              />
            )
          }
        : null,
      !userId
        ? {
            header: strings.columns.name,
            accessorKey: 'name',
            cell: info => <span>{info.getValue()}</span>
          }
        : null,
      {
        header: strings.columns.date,
        accessorKey: 'dateInfo.date',
        cell: info => {
          return <>{formatDate(new Date(info.getValue()), 'EEEE do MMMM')}</>;
        }
      },
      {
        header: strings.columns.started,
        accessorKey: 'dateInfo.start',
        cell: buildClockTime,
        sortingFn: (rowA, rowB) => {
          const startA = formatDate(rowA.original.dateInfo.start, 'HH:mm');
          const startB = formatDate(rowB.original.dateInfo.start, 'HH:mm');
          if (startA > startB) return 1;
          if (startB > startA) return -1;
          return 0;
        }
      },
      {
        header: strings.columns.ended,
        accessorKey: 'dateInfo.end',
        cell: buildClockTime,
        sortingFn: (rowA, rowB) => {
          const endA = rowA.original.dateInfo.end ? formatDate(rowA.original.dateInfo.end, 'HH:mm') : '';
          const endB = rowB.original.dateInfo.end ? formatDate(rowB.original.dateInfo.end, 'HH:mm') : '';
          if (endA > endB) return 1;
          if (endB > endA) return -1;
          return 0;
        }
      },
      {
        header: strings.columns.duration,
        accessorKey: 'dateInfo.duration',
        cell: info => {
          return <div className="whitespace-nowrap">{displayMinutesAsHoursMinutes(info.getValue() as number)}</div>;
        }
      },
      pay
        ? {
            header: strings.columns.estimatedCost,
            accessorKey: 'dateInfo.pay',
            cell: info => {
              return <div className="whitespace-nowrap">{displayPay(info.getValue() as number)}</div>;
            }
          }
        : null,
      locationList.length > 1
        ? {
            header: 'Location',
            accessorKey: 'locationId',
            cell: buildLocation
          }
        : null,
      {
        header: strings.columns.clockIn,
        accessorKey: 'clockInMethod',
        cell: buildClockMethod
      },
      {
        header: strings.columns.clockOut,
        accessorKey: 'clockOutMethod',
        cell: buildClockMethod
      },
      {
        header: () => null,
        id: 'expander',
        cell: ({ row }) => <TableExpander row={row} />
      }
    ];

    return optionalColumns.filter(c => c !== null) as ColumnDef<ShiftsTableRow>[];
  }, [buildClockTime, buildLocation, buildClockMethod, userId, pay, locationList]);

  const mobileColumns = useMemo(() => {
    const columns: ColumnDef<ShiftsTableRow>[] = [
      {
        header: strings.columns.when,
        accessorKey: 'dateInfo',
        cell: info => {
          const value = info.getValue() as ShiftsTableRow['dateInfo'];
          return <>{formatMobileDateTime(value.date, value.start, value.end)}</>;
        },
        sortingFn: (rowA, rowB) => {
          const startA = rowA.original.dateInfo.start.getTime();
          const startB = rowB.original.dateInfo.start.getTime();
          if (startA > startB) return 1;
          if (startB > startA) return -1;
          return 0;
        }
      },
      {
        header: strings.columns.risk,
        accessorKey: 'risk',
        cell: buildImportanceChit,
        sortingFn: sortRisk
      },
      {
        header: () => null,
        id: 'expander',
        cell: ({ row }) => <TableExpander row={row} />
      }
    ];

    if (!userId) {
      columns.unshift({
        header: strings.columns.name,
        accessorKey: 'name'
      });
    }

    return columns;
  }, [buildImportanceChit, sortRisk, userId]);

  return (
    <>
      <div className="hidden lg:block">
        <TableContents
          columns={desktopColumns}
          data={rows}
          DetailsView={ShiftDetailsView}
          pageSize={50}
          rowIdCallback={row => {
            return `${'row-' + row.original.record.id}`;
          }}
          autoExpandIdx={rows.findIndex(e => e.record.id === recordId)}
        />
      </div>
      <div className="lg:hidden">
        <TableContents
          columns={mobileColumns}
          data={rows}
          DetailsView={ShiftDetailsView}
          pageSize={50}
          rowIdCallback={row => {
            return `${'row-' + row.original.record.id}`;
          }}
          autoExpandIdx={rows.findIndex(e => e.record.id === recordId)}
        />
      </div>
    </>
  );
};
